import { ReactNode } from "react";

import useLabel from "@apps/use-label/use-label";
import AppLink from "@components/link/link";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";

import { useReadinessTools } from "./readiness-tools";

const StatusPill = ({ connected }: { connected: boolean }) => (
  <span
    className={`inline-flex shrink-0 items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs ${
      connected
        ? "border-green-200 bg-green-50 text-green-800"
        : "border-gray-200 bg-white text-gray-500"
    }`}
  >
    <span
      className={`h-1.5 w-1.5 rounded-full ${connected ? "bg-green-500" : "bg-gray-300"}`}
    />
    {connected ? "Connected" : "Not connected"}
  </span>
);

/**
 * Which work tools the organization has connected, what each adds to the
 * reviews, and where to connect the rest. One row per category, with Connect
 * beside the ones still missing for people who can connect them.
 *
 * `compact` is the summary's version: the categories side by side, each with
 * its logos and its Connect button or tag, and nothing to read.
 *
 * A category somebody here can connect is a card that links to the
 * Integrations settings page.
 */
const ReadinessIntegrations = ({
  organizationId,
  compact = false,
}: {
  organizationId: number;
  compact?: boolean;
}) => {
  const label = useLabel();
  const { categories, canManage } = useReadinessTools(organizationId);
  const integrationsUrl = `/settings/organization/${organizationId}/integrations`;

  // The logos say which tools the category means, with the name on hover,
  // instead of a line of names under it. Each in a small white circle with a
  // gray border, overlapping, with a white ring so each one stays distinct.
  const logos = (category: (typeof categories)[number]) => (
    <span className="flex items-center -space-x-1.5">
      {category.tools.map((tool) => (
        <span
          key={tool.name}
          title={tool.name}
          className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white ring-2 ring-white"
        >
          <img
            src={tool.logo}
            alt={tool.name}
            className={`h-3.5 w-3.5 ${tool.logoIsWordmark ? "object-cover object-left" : "object-contain"}`}
          />
        </span>
      ))}
    </span>
  );
  type Category = (typeof categories)[number];
  // Only a category somebody here can connect is a link; the rest have nothing
  // to click through to.
  const connectable = (category: Category) => canManage && !category.connected;
  // A Connect button already says a category is missing, so the tag is only for
  // the categories that have none. It is drawn as a button, not made one: the
  // whole card is the link, and a link cannot hold another.
  const status = (category: Category) =>
    connectable(category) ? (
      <span className="inline-flex h-[24px] shrink-0 items-center gap-1 rounded-[6px] bg-blue-50 px-2 text-sm font-medium text-blue-600 group-hover:bg-blue-100">
        <TopicflowIcon type="plus" className="h-4 w-4" />
        Connect
      </span>
    ) : (
      <StatusPill connected={category.connected} />
    );
  // Each category on a card of its own, like the cards in the sections above,
  // the whole card a link to the integrations page when there is something to
  // connect.
  const card = (category: Category, layout: string, children: ReactNode) =>
    connectable(category) ? (
      <AppLink
        to={integrationsUrl}
        className={`group rounded-md border bg-white transition-colors hover:border-gray-300 hover:bg-gray-50 ${layout}`}
      >
        {children}
      </AppLink>
    ) : (
      <div className={`rounded-md border bg-white ${layout}`}>{children}</div>
    );

  if (compact) {
    return (
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {categories.map((category) => (
          <li key={category.label} className="flex">
            {card(
              category,
              "flex w-full flex-col items-start gap-2 p-4",
              <>
                <span className="text-sm font-medium">{category.label}</span>
                {logos(category)}
                <div className="mt-auto pt-1">{status(category)}</div>
              </>,
            )}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div>
      <ul className="space-y-3">
        {categories.map((category) => (
          <li key={category.label}>
            {card(
              category,
              "flex items-center justify-between gap-4 px-5 py-4",
              <>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">
                      {category.label}
                    </span>
                    {logos(category)}
                  </div>
                  <div className="mt-0.5 text-sm text-gray-600">
                    {category.what}
                  </div>
                </div>
                {/* On the right, so every row's status reads down one
                    column. */}
                {status(category)}
              </>,
            )}
          </li>
        ))}
      </ul>
      {/* aggregate_context (chatbot/context_data.py) shares work tools and
          Slack only with the person and their managers; meetings go to anyone
          who can see them. */}
      {/* Plain text, so it does not read as one more category card. */}
      <div className="mt-3 text-sm text-gray-600">
        Generate Context draws on all of these tools in self and manager{" "}
        {label("review", { pluralize: true })}, and on meeting notes in peer and
        upward {label("review", { pluralize: true })}. The time saved assumes
        every tool helps in all four.
        {!canManage &&
          " An admin connects integrations on the Integrations settings page."}
      </div>
    </div>
  );
};

export default ReadinessIntegrations;
